// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as Sentry from '@sentry/react';
import { getUserFacingErrorMessage, handleAPIError } from '@/api/error-utils';
import {
  GenerateInvoicesResult,
  generateInvoices,
} from '@/billing/api/sweep-charges';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

interface UseGenerateInvoicesOptions {
  onSuccess?: (result: GenerateInvoicesResult) => void;
  onError?: (message: string) => void;
}

export function useGenerateInvoices(options?: UseGenerateInvoicesOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => generateInvoices(),
    onSuccess: async (response) => {
      // The sweep rebinds charges onto existing draft invoices or mints
      // new ones, so every invoice-backed view can shift. Skip the
      // per-invoice audit invalidation (handled on open) because sweep is a
      // bulk operation and the affected invoice keys aren't known here.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['charges'] }),
        queryClient.invalidateQueries({ queryKey: ['invoices'] }),
        queryClient.invalidateQueries({ queryKey: ['billing-summary'] }),
      ]);
      options?.onSuccess?.(response.data);
    },
    onError: (error: AxiosError) => {
      const apiError = handleAPIError(error);
      if (!apiError.sentToSentry) {
        Sentry.captureException(error);
      }
      options?.onError?.(getUserFacingErrorMessage(apiError));
    },
  });
}
